import { ref, onUnmounted } from 'vue'
import { getAuth, signInWithEmailAndPassword, signOut, onAuthStateChanged } from 'firebase/auth'
import type { User } from 'firebase/auth'
import { useLogger } from './useLogger'

export function useAuth() {
  const user = ref<User | null>(null)
  const loading = ref(true)
  const error = ref<string | null>(null)
  const auth = getAuth()
  const logger = useLogger()

  const unsubscribe = onAuthStateChanged(auth, newUser => {
    user.value = newUser
    loading.value = false
    logger.info('Estado de autenticación actualizado', {
      component: 'useAuth',
      method: 'onAuthStateChanged',
      data: { userId: newUser?.uid },
    })
  })

  onUnmounted(() => {
    unsubscribe()
    logger.debug('Listener de autenticación desmontado', {
      component: 'useAuth',
      method: 'onUnmounted',
    })
  })

  const login = async (email: string, password: string) => {
    const isDev = import.meta.env.VITE_DEV === 'true'
    const useEmulator = import.meta.env.VITE_USE_EMULATOR === 'true'

    // Solo aplicar restricción en desarrollo local (no en emulador)
    if (isDev && !useEmulator && email !== 'test@accyon.com') {
      const msg = 'Solo el usuario de test puede autenticarse en modo desarrollo local.'
      const appError = logger.createError(msg, 'AUTH_RESTRICTED', {
        component: 'useAuth',
        method: 'login',
        data: { email, isDev, useEmulator },
      })
      error.value = msg
      throw appError
    }

    try {
      error.value = null
      await signInWithEmailAndPassword(auth, email, password)
      logger.info('Usuario autenticado exitosamente', {
        component: 'useAuth',
        method: 'login',
        data: { email, isDev, useEmulator },
      })
    } catch (e: unknown) {
      const appError = logger.handleError(e instanceof Error ? e : new Error(String(e)), {
        component: 'useAuth',
        method: 'login',
        data: { email, isDev, useEmulator },
      })
      error.value = appError.message
      throw appError
    }
  }

  const logout = async () => {
    try {
      error.value = null
      await signOut(auth)
      logger.info('Usuario desconectado exitosamente', {
        component: 'useAuth',
        method: 'logout',
        data: { userId: user.value?.uid },
      })
    } catch (e: unknown) {
      const appError = logger.handleError(e instanceof Error ? e : new Error(String(e)), {
        component: 'useAuth',
        method: 'logout',
        data: { userId: user.value?.uid },
      })
      error.value = appError.message
      throw appError
    }
  }

  return {
    user,
    loading,
    error,
    login,
    logout,
  }
}
